import * as Sentry from '@sentry/browser';
import 'Components/atoms/vm-bottom-sheet/vm-bottom-sheet';
import type { VmBottomSheet } from 'Components/atoms/vm-bottom-sheet/vm-bottom-sheet';
import 'Components/atoms/vm-button/vm-button';
import 'Components/atoms/vm-loading-spinner/vm-loading-spinner';
import { CommentsSheetController } from 'Components/controllers/comments-sheet-controller';
import 'Video/components/vm-video-comment-composer/vm-video-comment-composer';
import './vm-video-comments-list';
import { LitElement, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { createInfiniteQueryController } from '@tanstack/lit-query';
import type { InfiniteData } from '@tanstack/query-core';
import { ApiFetchError } from 'Utils/api-fetch-request';
import { DESKTOP_BREAKPOINT_QUERY } from 'Utils/viewport';
import { VmVideoCommentComposer } from 'Video/components/vm-video-comment-composer/vm-video-comment-composer';
import { commentsQueryOptions, postComment, type CommentData, type CommentsApiResponse } from 'Api/comments';
import { queryClient } from 'Api/query-client';
import { VideoCommentEvents, trackVideoCommentEvent } from 'Analytics/video-comment-events';
import Styles from './vm-video-comments.scss';

/**
 * Appends `reply` to the root-level `children[]` of whichever root either
 * matches `parentId` or contains it as an existing reply. Mirrors the server
 * read shape: `CommentThreadSerializer` flattens every descendant of a tree
 * into the root's `children` array, so the client only ever sees depth ≤ 1.
 * Inserting at the immediate-parent's depth (as a naïve recursive walk would)
 * creates a depth-2 node optimistically, which then visibly "jumps up" a
 * level on the next refetch when the server re-flattens it. Returns a new
 * tree (immutable — safe for setQueryData).
 */
function insertReplyIntoTree(nodes: CommentData[], parentId: number, reply: CommentData): CommentData[] {
    return nodes.map((node) => {
        const isRoot = node.id === parentId;
        const containsReply = node.children?.some((child) => child.id === parentId) ?? false;
        if (isRoot || containsReply) {
            return { ...node, children: [...(node.children ?? []), reply] };
        }
        return node;
    });
}

/**
 * `setQueryData` updater for the comments infinite cache. Empty cache seeds a
 * single page; a reply walks every page and inserts under its parent (page 0
 * gets a `total_count` bump but not `count`); a top-level post prepends into
 * page 0 and bumps both counts.
 */
export function applyPostedToCache(
    old: InfiniteData<CommentsApiResponse, number> | undefined,
    posted: CommentData,
    replyToId: number | null,
): InfiniteData<CommentsApiResponse, number> {
    if (!old) {
        // No cache yet. For a top-level post seed a single page with it;
        // for a reply with no cache (odd but possible), just bump total_count.
        return {
            pages: [
                {
                    count: replyToId ? 0 : 1,
                    total_count: 1,
                    results: replyToId ? [] : [posted],
                },
            ],
            pageParams: [0],
        };
    }
    if (replyToId) {
        // Walk every page and insert under the parent wherever it lives.
        // Bump `total_count` (all-inclusive) on page 0 but NOT `count`
        // (top-level only, drives `hasNextPage`).
        const updatedPages = old.pages.map((page) => ({
            ...page,
            results: insertReplyIntoTree(page.results, replyToId, posted),
        }));
        if (updatedPages[0]) {
            updatedPages[0] = {
                ...updatedPages[0],
                total_count: updatedPages[0].total_count + 1,
            };
            return { ...old, pages: updatedPages };
        }
        // Defensive: cache exists but has zero pages. Seed a synthetic page 0
        // matching the `!old` reply shape so the reply-count still ticks up
        // and we keep TanStack's `pages.length === pageParams.length` invariant.
        return {
            ...old,
            pages: [{ count: 0, total_count: 1, results: [] }],
            pageParams: [0],
        };
    }
    // Top-level — prepend to page 0. Bump both counts.
    const [firstPage, ...restPages] = old.pages;
    const updatedFirst: CommentsApiResponse = {
        count: (firstPage?.count ?? 0) + 1,
        total_count: (firstPage?.total_count ?? 0) + 1,
        results: [posted, ...(firstPage?.results ?? [])],
    };
    // Seed `pageParams` on the fallback branch (`pages: []` → empty
    // `pageParams`) so we keep TanStack's `pages.length === pageParams.length`
    // invariant; otherwise `getNextPageParam` sees `lastPageParam=undefined`.
    const pageParams = old.pageParams.length > 0 ? old.pageParams : [0];
    return { ...old, pages: [updatedFirst, ...restPages], pageParams };
}

/**
 * Video-page comments organism. Reads view/add permissions from the SSR-set
 * `can-view-comments` / `can-add-comments` attributes (Django knows both at
 * render time — no `/api/me` round-trip) and renders a loading spinner, a
 * "comments disabled" notice, an anonymous login card, or the list.
 *
 * SSR hydration: when the Django template sets the `data-initial-comments`
 * attribute with the first page of comments, `_hydrateFromInitial()` seeds
 * the tanstack cache synchronously in `connectedCallback` — no
 * `/api/videos/<slug>/comments/` round-trip on first paint. Missing/malformed
 * payloads fall back to fetching page 0 with the spinner up.
 *
 * Pagination via `@tanstack/lit-query`'s `createInfiniteQueryController` — an
 * `IntersectionObserver` at the bottom of the list calls
 * `_commentsQuery.fetchNextPage()` when the load-more element intersects.
 * `_items` flattens `data.pages[]` and dedups by id (a submit landing
 * server-side between fetches shifts the offset by one and can surface a
 * boundary comment twice).
 *
 * Mobile (≤ desktop breakpoint) wraps the list in `<vm-bottom-sheet>` opened by
 * `vm-video-comments-button:open` events on `document`; the composer renders
 * inside the sheet body and is pinned to its visible viewport via
 * `position: sticky`. On composer focus the sheet flips to `fullscreen` and
 * grows to `visualViewport.height`, keeping the header pinned above the iOS
 * on-screen keyboard for the rest of the sheet's open lifetime (`_sheetFullscreen`).
 * Desktop renders inline below the player. Anonymous users skip the sheet on
 * both viewports — the inline login card carries the count sourced from the
 * SSR seed and a "Login or sign up" CTA.
 *
 * Submit: `<vm-video-comment-composer>` dispatches `vm-comment-submit`; this
 * host catches it (bubbling + composed, so inline reply composers are caught
 * too), flips the specific dispatching composer's `loading` via
 * `event.composedPath()[0]`, POSTs the comment with the detail's
 * `attachmentIds` (and `parent` if a reply) — attachments themselves are
 * uploaded inside the composer on pick, so the ids in the detail are already
 * server-assigned by the time we get here — then clears that composer and
 * writes the result into the infinite query's cache via
 * `queryClient.setQueryData`. Failure preserves the draft. User avatar / name
 * / profile link come from `user-*` attrs (Django-rendered).
 *
 * Reply mode is split by viewport:
 * - Desktop: each list-item owns its own inline reply composer (see the
 *   list-item's `_replyOpen` state and document-event coordination). The
 *   organism only participates by POSTing and dispatching
 *   `vm-video-comment-reply:posted` on success — items self-close.
 * - Mobile: list-items bubble `vm-video-comment-reply:select`; the organism
 *   opens the bottom sheet, focuses the top composer, and shows the "Replying
 *   to <Name>" chip via `_replyToId` / `_replyToName`. Composer's
 *   `vm-comment-reply-cancel` (chip ✕) nulls the reply state.
 *
 * @fires vm-video-comments:sheet-visibility - bubbles + composed. Detail:
 *   `{ open: boolean }`. Fires on the mobile-sheet open/close transition;
 *   OPEN is always mobile (`_sheetOpen` only flips to `true` on mobile), and
 *   CLOSE also fires on a mobile→desktop resize or teardown of a still-open
 *   sheet. Distinct from the SHEET_OPENED / SHEET_CLOSED Mixpanel events
 *   (pure analytics).
 * @fires vm-video-comment-reply:posted - CustomEvent on document. Fired after
 *   a successful reply POST with `{ parentId }`. Consumed by list-items to
 *   close their inline reply composer.
 */
@customElement('vm-video-comments')
export class VmVideoComments extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'login-url' }) loginUrl = '';
    @property({ type: String, attribute: 'video-slug' }) videoSlug = '';
    @property({ type: Boolean, attribute: 'disable-comments' }) disableComments = false;
    @property({ type: Boolean, attribute: 'user-authenticated' }) userAuthenticated = false;
    @property({ type: String, attribute: 'user-avatar-src' }) userAvatarSrc = '';
    @property({ type: String, attribute: 'user-name' }) userName = '';
    @property({ type: String, attribute: 'user-profile-url' }) userProfileUrl = '';
    /** SSR-set (`{% if perms.comment.view_comments %}can-view-comments{% endif %}`);
     * gates the list + the fetch. Falsy → render the login card (anon) or nothing (authed w/o perm). */
    @property({ type: Boolean, attribute: 'can-view-comments' }) canViewComments = false;
    /** SSR-set (`{% if perms.comment.add_comment %}can-add-comments{% endif %}`);
     * gates the composer + reply buttons on each list item. */
    @property({ type: Boolean, attribute: 'can-add-comments' }) canAddComments = false;

    @state() dataFetched = false;
    @state() private _isDesktop = false;
    @state() private _sheetOpen = false;
    /**
     * On composer focus, we grow the mobile sheet to `visualViewport.height`
     * and keep it there for the rest of the sheet's open lifetime. Sidesteps
     * the iOS Safari "sheet header scrolls off-screen" issue where iOS scrolls
     * the layout viewport to bring the focused input into the visual viewport,
     * hiding the top of the sheet. Reset in `_setSheetOpen(false)`.
     */
    @state() private _sheetFullscreen = false;
    @state() private _replyToId: number | null = null;
    @state() private _replyToName = '';

    @query('vm-video-comment-composer') private _composer?: VmVideoCommentComposer;
    @query('.comments__load-more') private _loadMoreEl?: HTMLElement;
    @query('vm-bottom-sheet') private _sheet?: VmBottomSheet;

    private _mq: MediaQueryList | null = null;
    /** The `.comments__load-more` element we're currently observing. Kept in
     * sync so a desktop↔mobile render-tree swap (inline `<div>` ↔ `<vm-bottom-sheet>`)
     * re-attaches the observer to the new element instead of stranding it on the old. */
    private _observedLoadMoreEl: HTMLElement | null = null;
    /** The `root` the load-more observer is currently bound to. Tracked so that
     * a viewport→sheet-body transition (the sheet body isn't in the DOM yet on
     * the organism's first `updated()` — vm-bottom-sheet renders one microtask
     * later) rebuilds the observer with the correct root. Without this the
     * observer would stay viewport-rooted and the 200px prefetch margin would
     * be measured against the wrong container. */
    private _observedRoot: Element | null = null;
    private _loadMoreObserver: IntersectionObserver | null = null;

    /**
     * `enabled` gates on permissions + slug so the observer stays idle until
     * we're ready to fetch. Accessor form is safe as of @tanstack/lit-query 0.2.9+
     * (older versions had a re-render loop via `getOptimisticResult`).
     */
    private _commentsQuery = createInfiniteQueryController(
        this,
        () => ({
            ...commentsQueryOptions(this.videoSlug),
            enabled: !!this.videoSlug && this.canViewComments,
        }),
        queryClient,
    );

    /** Flattened + dedup'd top-level comments across all fetched pages. */
    private get _items(): CommentData[] {
        const pages = this._commentsQuery.current.data?.pages ?? [];
        const seen = new Set<number>();
        const out: CommentData[] = [];
        // A submit landing server-side between fetches can shift the offset by one,
        // so the same comment can appear in two adjacent pages. Dedup by id keeps
        // the final list clean.
        for (const page of pages) {
            // `results` is normally always an array on well-formed pages, but a
            // malformed response (e.g. a fetch stub or partial-JSON error path)
            // can leave it undefined. Fall back to an empty array so a single
            // bad page can't blow up the whole render.
            for (const c of page.results ?? []) {
                if (!seen.has(c.id)) {
                    seen.add(c.id);
                    out.push(c);
                }
            }
        }
        return out;
    }

    private get _totalComments(): number {
        return this._commentsQuery.current.data?.pages[0]?.total_count ?? 0;
    }

    private get _hasMore(): boolean {
        return this._commentsQuery.current.hasNextPage;
    }

    private get _loadingMore(): boolean {
        return this._commentsQuery.current.isFetchingNextPage;
    }

    constructor() {
        super();

        new CommentsSheetController(this, {
            // Ignore opens on desktop — `_sheetOpen` would otherwise strand
            // `true` and pop the sheet on a later resize back to mobile.
            onOpen: () => {
                if (this._isDesktop) return;
                this._setSheetOpen(true);
            },
        });
    }

    private _onMqChange = (e: MediaQueryListEvent) => {
        this._isDesktop = e.matches;
        // Force-close on desktop edge so `_sheetOpen` doesn't strand `true`,
        // and clear reply state so it doesn't leak into the top composer once
        // desktop's inline-per-item reply flow takes over.
        if (this._isDesktop) {
            this._setSheetOpen(false);
            this._onReplyCancel();
        }
    };

    /**
     * Mobile-only. On desktop, the item opens its own inline composer without
     * involving the organism (see the list-item's `handleReplyClick`).
     */
    private _onReplySelect = (e: CustomEvent<{ commentId: number; authorName: string }>) => {
        // Belt-and-suspenders: the list-item only dispatches this on mobile,
        // but the organism should refuse to open the sheet on desktop even if
        // the event arrives (e.g. from a stray test dispatch or programmatic
        // caller). Prevents `_sheetOpen: true` on desktop.
        if (this._isDesktop) return;
        this._replyToId = e.detail.commentId;
        this._replyToName = e.detail.authorName;
        this._setSheetOpen(true);
        this.updateComplete.then(() => {
            this._composer?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            this._composer?.focus();
        });
    };

    private _onReplyCancel = () => {
        this._replyToId = null;
        this._replyToName = '';
    };

    private _onComposerFocus = () => {
        if (this._isDesktop || !this._sheetOpen) return;
        this._sheetFullscreen = true;
    };

    private _onCommentSubmit = async (
        e: CustomEvent<{ text: string; attachmentIds: number[]; replyToId: number | null }>,
    ) => {
        const { text, attachmentIds, replyToId } = e.detail;
        if (!text.trim() && attachmentIds.length === 0) return;

        // Take over the submit so the composer keeps the draft until the POST resolves.
        e.preventDefault();
        // The originating composer sits at the deepest end of the composed path
        // (event.target retargets across shadow boundaries). Flipping its own
        // `loading` prop keeps the spinner scoped to it — the other composer
        // (top or inline) stays interactive.
        const composer = e.composedPath()[0];
        const targetComposer = composer instanceof VmVideoCommentComposer ? composer : null;
        if (targetComposer) targetComposer.loading = true;

        // Try guards only the POST. Uploads are the composer's responsibility
        // (fired on pick, tracked per-file via ATTACHMENT_UPLOAD_ATTEMPTED /
        // _FAILED); by the time we get here they've already succeeded.
        // Mixpanel / clear / cache write run after so an analytics throw doesn't
        // surface "Could not post" for a persisted comment.
        let response: CommentData | null = null;
        try {
            response = await postComment({
                videoSlug: this.videoSlug,
                comment: text,
                attachmentIds,
                parentId: replyToId ?? undefined,
            });
        } catch (error) {
            // Wrapper auto-reports 5xx; don't double-report. 4xx stays silent (CLAUDE.md).
            if (!(error instanceof ApiFetchError)) Sentry.captureException(error);
            document.dispatchEvent(
                new CustomEvent('vm-toast-show', {
                    detail: { message: 'Could not post comment. Please try again.', variant: 'error' },
                }),
            );
        } finally {
            if (targetComposer) targetComposer.loading = false;
        }

        if (!response) return;

        targetComposer?.clear();
        this._replyToId = null;
        this._replyToName = '';

        // Signal to any open inline composer keyed on this parent to close.
        // (Desktop-only path; mobile has no inline composer to close.)
        if (replyToId) {
            document.dispatchEvent(
                new CustomEvent('vm-video-comment-reply:posted', { detail: { parentId: replyToId } }),
            );
        }

        // Post/reply Mixpanel — `trackVideoCommentEvent` auto-enriches with the
        // video-detail context. `top_level_comment_id` is threaded through only
        // when the composer supplies it — the ST-1517 composer emits `replyToId`
        // only (the immediate parent) and resolving the top-level ancestor
        // would require walking the tanstack cache; consumers that need the
        // top-level context can wire it in a follow-up.
        const coreProps = {
            comment_id: response.id || null,
            is_public: response.is_public ?? null,
            attachment_count: response.attachments?.length || 0,
        };
        if (replyToId) {
            trackVideoCommentEvent(VideoCommentEvents.REPLY_POSTED, {
                ...coreProps,
                reply_to_comment_id: replyToId,
            });
        } else {
            trackVideoCommentEvent(VideoCommentEvents.POSTED, coreProps);
        }

        // Write the new comment into the infinite query's cache. Cancel any
        // in-flight fetch on this key first so its response can't clobber the
        // optimistic write (narrow race between mount and initial GET).
        const key = commentsQueryOptions(this.videoSlug).queryKey;
        await queryClient.cancelQueries({ queryKey: key });
        queryClient.setQueryData<InfiniteData<CommentsApiResponse, number>>(key, (old) =>
            applyPostedToCache(old, response, replyToId),
        );
    };

    /**
     * Single point of control for `_sheetOpen` transitions. Runs synchronously
     * at the mutation site, so analytics and the DOM event are dispatched
     * before the next microtask — a nav-away teardown still emits its CLOSE,
     * and there's no `updated()`-observer race with a swapped-out test stub.
     *
     * Dispatch target: normally `this` (bubbles+composed reaches document
     * listeners). Once detached — the teardown flush from
     * `disconnectedCallback` — the host has no ancestor chain, so a bubbling
     * event dispatched from `this` never reaches `document`. Fall back to
     * dispatching on `document` directly so an orchestrator that outlives the
     * organism still sees the final `open: false` and can clear its state.
     */
    private _setSheetOpen(open: boolean): void {
        if (this._sheetOpen === open) return;
        this._sheetOpen = open;
        // Reset the "grew to fullscreen on composer focus" flag when the sheet
        // closes so the next open starts back at the initial height.
        if (!open) this._sheetFullscreen = false;
        trackVideoCommentEvent(open ? VideoCommentEvents.SHEET_OPENED : VideoCommentEvents.SHEET_CLOSED, {});
        const target = this.isConnected ? this : document;
        target.dispatchEvent(
            new CustomEvent('vm-video-comments:sheet-visibility', {
                bubbles: true,
                composed: true,
                detail: { open },
            }),
        );
    }

    override async connectedCallback() {
        super.connectedCallback();

        this._mq = window.matchMedia(DESKTOP_BREAKPOINT_QUERY);
        this._isDesktop = this._mq.matches;
        this._mq.addEventListener('change', this._onMqChange);

        // Composer events are caught at the host so both the top composer AND
        // any inline reply composer rendered inside a list-item are handled.
        this.addEventListener('vm-video-comment-reply:select', this._onReplySelect);
        this.addEventListener('vm-comment-submit', this._onCommentSubmit);
        this.addEventListener('vm-comment-reply-cancel', this._onReplyCancel);
        this.addEventListener('vm-video-comment-composer:focus', this._onComposerFocus);

        if (this.videoSlug) {
            // Hydrate for everyone — the seed carries `count` / `total_count`
            // for anon + no-perm users too (empty `results`), and the anon
            // card reads its label from the cache.
            const hydrated = this._hydrateFromInitial();
            if (!hydrated && this.canViewComments) {
                // No / malformed seed — fetch page 0. Await it so the spinner
                // covers the fetch instead of flashing an empty header.
                try {
                    await queryClient.fetchInfiniteQuery(commentsQueryOptions(this.videoSlug));
                } catch {
                    // Errors are surfaced via the controller.
                }
            }
        }
        this.dataFetched = true;
    }

    /**
     * Seed the tanstack query cache from the SSR-emitted first page. Returns
     * `true` when hydration succeeded. The seed is read from the
     * `data-initial-comments` attribute set by the video-detail template
     * (`{% initial_comments_json video as comments_json %}`). Django's default
     * autoescape entity-encodes the JSON for HTML attribute context, so
     * user comment content can't break out of the attribute; `dataset` decodes
     * the entities back on read so `JSON.parse` sees the original payload.
     * Bails on missing/malformed payloads so a bad SSR emit degrades to the
     * normal fetch path instead of corrupting the cache.
     */
    private _hydrateFromInitial(): boolean {
        const raw = this.dataset.initialComments;
        if (!raw) return false;
        let seed: CommentsApiResponse;
        try {
            seed = JSON.parse(raw) as CommentsApiResponse;
        } catch {
            return false;
        }
        if (
            !seed ||
            typeof seed.count !== 'number' ||
            typeof seed.total_count !== 'number' ||
            !Array.isArray(seed.results)
        ) {
            return false;
        }
        queryClient.setQueryData<InfiniteData<CommentsApiResponse, number>>(
            commentsQueryOptions(this.videoSlug).queryKey,
            { pages: [seed], pageParams: [0] },
        );
        return true;
    }

    override updated() {
        this._setupLoadMoreObserver();
    }

    override disconnectedCallback() {
        // Flush a pending CLOSE if the sheet was open at teardown (page nav,
        // element removal). Runs synchronously so the event still lands on the
        // correct mixpanel binding, and `_setSheetOpen` falls back to
        // `document.dispatchEvent` when the host is already detached — so an
        // orchestrator that outlives the organism still sees `open: false`.
        this._setSheetOpen(false);
        this._loadMoreObserver?.disconnect();
        this._loadMoreObserver = null;
        this._mq?.removeEventListener('change', this._onMqChange);
        this._mq = null;
        this.removeEventListener('vm-video-comment-reply:select', this._onReplySelect);
        this.removeEventListener('vm-comment-submit', this._onCommentSubmit);
        this.removeEventListener('vm-comment-reply-cancel', this._onReplyCancel);
        this.removeEventListener('vm-video-comment-composer:focus', this._onComposerFocus);
        super.disconnectedCallback();
    }

    private _setupLoadMoreObserver(): void {
        // On mobile the list scrolls inside `vm-bottom-sheet.__body`, so use
        // it as the observer `root` — keeps intersection + the 200px prefetch
        // margin measured against the real scroll container (desktop uses the
        // viewport). Null until the sheet's first render commits; the
        // follow-up `updated()` below re-runs and picks it up.
        const root = this._isDesktop ? null : (this._sheet?.scrollContainer ?? null);

        // Bail if neither the load-more element nor the intended root has
        // changed. `root` is fixed at construction, so any transition (both on
        // desktop↔mobile swap AND on the viewport→sheet-body upgrade) means we
        // have to disconnect and rebuild.
        if (this._loadMoreEl === this._observedLoadMoreEl && root === this._observedRoot) return;

        this._loadMoreObserver?.disconnect();
        this._loadMoreObserver = null;
        this._observedLoadMoreEl = this._loadMoreEl ?? null;
        this._observedRoot = root;

        if (!this._loadMoreEl) return;

        this._loadMoreObserver = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && this._hasMore && !this._loadingMore) {
                    this._commentsQuery.fetchNextPage();
                }
            },
            { root, rootMargin: '200px' },
        );
        this._loadMoreObserver.observe(this._loadMoreEl);

        // Mobile: the sheet's shadow root isn't ready on our first `updated()`
        // (Lit queues its update one microtask later). Rerun ourselves once
        // it commits. Strict `=== false` skips unupgraded custom elements
        // (props still undefined), which would otherwise look "pending" via
        // `!hasUpdated`.
        if (!this._isDesktop && this._sheet?.hasUpdated === false) {
            this._sheet.updateComplete.then(() => this.requestUpdate());
        }
    }

    override render() {
        if (!this.dataFetched) {
            return html`
                <div class="comments">
                    <vm-loading-spinner size="xl"></vm-loading-spinner>
                </div>
            `;
        }

        if (this.disableComments) {
            return html`
                <div class="comments">
                    <span class="comments__title">Comments are disabled for this content.</span>
                </div>
            `;
        }

        // Anonymous → inline card with count + login CTA (no sheet on mobile —
        // anon has no way to open one).
        if (!this.userAuthenticated) {
            return this._renderAnonCard();
        }

        // Authed but no view permission → render nothing (they see comments
        // hidden by policy, not by login-gate).
        if (!this.canViewComments) {
            return nothing;
        }

        // Mobile: list + composer inside the sheet body; composer is pinned to
        // the body's visible viewport via SCSS. Desktop: inline.
        if (!this._isDesktop) {
            return html`
                <vm-bottom-sheet
                    ?open=${this._sheetOpen}
                    ?fullscreen=${this._sheetFullscreen}
                    @vm-close=${() => {
                        this._setSheetOpen(false);
                        // Clear reply state on dismiss so a subsequent open of
                        // the top composer doesn't inherit "Replying to Jane"
                        // and silently POST as `parent=<Jane's id>`.
                        this._onReplyCancel();
                    }}
                >
                    <span slot="header">Comments</span>
                    <div class="comments">${this._renderList()} ${this._renderComposer()}</div>
                </vm-bottom-sheet>
            `;
        }

        return html`
            <div class="comments">
                <p class="comments__title">${this._totalComments} Comments</p>
                ${this._renderComposer()} ${this._renderList()}
            </div>
        `;
    }

    private _renderAnonCard() {
        const label = this._totalComments === 1 ? '1 comment' : `${this._totalComments} comments`;
        return html`
            <div class="comments">
                <div class="comments__login-card">
                    <p class="comments__login-card-text">
                        <strong>${label}</strong>
                        <span class="comments__login-card-sep">•</span>
                        <span>Login or sign up to view comments.</span>
                    </p>
                    ${this.loginUrl
                        ? html`
                              <vm-button variant="solid" color-scheme="primary" size="sm" href=${this.loginUrl}>
                                  Login or sign up
                              </vm-button>
                          `
                        : nothing}
                </div>
            </div>
        `;
    }

    private _renderComposer() {
        if (!this.canAddComments) return nothing;
        // On desktop, reply mode is handled inline (composer beneath the target
        // comment), so the top composer never carries reply state. On mobile,
        // the top composer is the only composer — pass reply state through.
        return html`
            <vm-video-comment-composer
                video-slug=${this.videoSlug}
                avatar-src=${this.userAvatarSrc}
                avatar-name=${this.userName}
                avatar-link=${this.userProfileUrl}
                .replyToId=${this._isDesktop ? null : this._replyToId}
                .replyToName=${this._isDesktop ? '' : this._replyToName}
            ></vm-video-comment-composer>
        `;
    }

    private _renderList() {
        // `view_comments` is guaranteed by the top-level render() gate — anon
        // and no-permission cases return before we get here.
        return html`
            <div class="comments__wrapper">
                <vm-video-comments-list
                    .commentsData=${this._items}
                    .dataAddCommentsPerm=${this.canAddComments}
                    .videoSlug=${this.videoSlug}
                    .userAvatarSrc=${this.userAvatarSrc}
                    .userAvatarName=${this.userName}
                    .userProfileUrl=${this.userProfileUrl}
                ></vm-video-comments-list>
                ${this._hasMore
                    ? html`
                          <div class="comments__load-more">
                              ${this._loadingMore ? html`<vm-loading-spinner size="lg"></vm-loading-spinner>` : nothing}
                          </div>
                      `
                    : nothing}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-comments': VmVideoComments;
    }
}
